import {useReactFlow} from "@xyflow/react";
import {useRef, useCallback} from "react";
import {debounce} from "lodash";

// Recursive function to update a parameter by key in nested objects
const updateParamRecursively = (
  obj: any,
  paramKey: string,
  paramValue: any,
): any => {
  if (obj === null || obj === undefined) return obj;

  if (typeof obj === "object" && !Array.isArray(obj)) {
    // If this object has a 'key' property that matches our target
    if (obj.key === paramKey) {
      return {...obj, value: paramValue};
    }

    // Recursively update all properties of this object
    const updatedObj = {...obj};
    for (const key in updatedObj) {
      if (Object.prototype.hasOwnProperty.call(updatedObj, key)) {
        updatedObj[key] = updateParamRecursively(
          updatedObj[key],
          paramKey,
          paramValue,
        );
      }
    }
    return updatedObj;
  }

  if (Array.isArray(obj)) {
    // Recursively update all elements in the array
    return obj.map(item => updateParamRecursively(item, paramKey, paramValue));
  }

  // Return primitive values as-is
  return obj;
};

const NODE_UPDATE_DEBOUNCE_MS = 100;

export const useNodeUpdater = () => {
  const {setNodes} = useReactFlow();
  const debouncedSetNodesRef = useRef<ReturnType<typeof debounce> | null>(null);

  const updateNodeSettingParam = useCallback(
    (id: string, paramKey: string, paramValue: any) => {
      // Create debounced function if it doesn't exist
      if (!debouncedSetNodesRef.current) {
        debouncedSetNodesRef.current = debounce(nodesUpdater => {
          setNodes(nodesUpdater);
        }, NODE_UPDATE_DEBOUNCE_MS);
      }

      // Call the debounced function
      debouncedSetNodesRef.current(nodes =>
        nodes.map(node => {
          if (node.id !== id) return node;

          const updatedData = updateParamRecursively(
            node.data,
            paramKey,
            paramValue,
          );

          // Update isEmpty based on settings if they exist
          let isEmpty = true;
          if (updatedData.settings && Array.isArray(updatedData.settings)) {
            isEmpty = updatedData.settings.every(
              setting =>
                !setting.params.some(
                  param =>
                    param.value !== undefined &&
                    param.value !== "" &&
                    (!Array.isArray(param.value) || param.value.length > 0),
                ),
            );
          }

          return {
            ...node,
            data: {
              ...updatedData,
              isEmpty,
            },
          };
        }),
      );
    },
    [setNodes],
  );

  return {updateNodeSettingParam};
};
